import React, { useState, useEffect } from 'react';
import {
  Search, BookOpen, Heart, Target, Trophy, Check, Mail, Phone, MapPin,
  Sparkles, Globe, Users, Award, ArrowRight, TrendingUp, Zap, Star, Rocket,
  Brain, X, Instagram, Facebook, Linkedin
} from 'lucide-react';

import { ThemeProvider } from './ThemeContext';
import Hero from './Hero';
//import Services from './Services';
import Specializations from './Specializations';
import Platform from './Platform';
import Testimonials from './Testimonials';
import Contact from './Contact';
// REMOVED: import Footer from '../Footer/Footer';
import SuccessModal from './SuccessModal';
import Header from '../Header/header';
import ContactPopup from './Popup'; // ✅ Corrected import

export default function Home() {
  const [selectedCountry, setSelectedCountry] = useState('');
  const [customCountry, setCustomCountry] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isVisible, setIsVisible] = useState({});
  const [scrollY, setScrollY] = useState(0);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showPopup, setShowPopup] = useState(false); // ✅ Added state for the new popup

  const countries = [
    'India', 'USA', 'UK', 'Canada', 'Australia', 'Germany',
    'France', 'Singapore', 'New Zealand', 'Ireland', 'Other'
  ];

  const subjectOptions = ['Partnership', 'Admission Inquiry', 'Career Counseling'];

  const stats = [
    { number: '10,000+', label: 'Universities', icon: <Globe className="w-8 h-8" />, gradient: 'from-blue-500 to-cyan-500' },
    { number: '30+', label: 'Countries', icon: <Award className="w-8 h-8" />, gradient: 'from-purple-500 to-pink-500' },
    { number: '50,000+', label: 'Students Placed', icon: <Users className="w-8 h-8" />, gradient: 'from-orange-500 to-red-500' },
    { number: '100%', label: 'Commission Basis', icon: <TrendingUp className="w-8 h-8" />, gradient: 'from-green-500 to-emerald-500' }
  ];

  const features = [
    { title: 'AI-Powered Platform', desc: 'Advanced AI algorithms to match students with perfect educational opportunities', icon: <Brain className="w-8 h-8" />, gradient: 'from-purple-500 to-pink-500' },
    { title: 'Marketing Analytics', desc: 'Comprehensive marketing management with integrated social media capabilities', icon: <TrendingUp className="w-8 h-8" />, gradient: 'from-blue-500 to-cyan-500' },
    { title: 'Lead Management', desc: 'Automated lead management system to streamline student recruitment', icon: <Users className="w-8 h-8" />, gradient: 'from-green-500 to-emerald-500' },
    { title: 'Workzon Integration', desc: 'Built-in workpad for efficient task management and seamless workflow', icon: <Zap className="w-8 h-8" />, gradient: 'from-yellow-500 to-orange-500' },
    { title: 'Colabzon Collaboration', desc: 'In-built Slack-like capabilities for team collaboration', icon: <Globe className="w-8 h-8" />, gradient: 'from-indigo-500 to-purple-500' },
    { title: 'Performance Tracking', desc: 'Advanced KPI tracking, team targets, and incentive management', icon: <Award className="w-8 h-8" />, gradient: 'from-red-500 to-pink-500' }
  ];

  const testimonials = [
    { name: 'Priya Sharma', role: 'MBA Student, University of Toronto', text: 'Vidyazon made my dream of studying abroad a reality. Their AI-powered matching helped me find the perfect program, and the counseling team supported me throughout the entire process.', gradient: 'from-blue-500 to-cyan-500' },
    { name: 'Rajesh Kumar', role: 'Engineering Student, IIT Delhi', text: 'The coaching and preparation services at Vidyazon are exceptional. Their personalized approach and expert guidance helped me crack JEE with flying colors.', gradient: 'from-purple-500 to-pink-500' },
    { name: 'Anitha Reddy', role: 'Career Counselor, Hyderabad', text: 'As a partner, Vidyazon\'s platform has transformed how we manage students and track performance. The automation features save us hours of work every day.', gradient: 'from-orange-500 to-red-500' }
  ];

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }));
        });
      },
      { threshold: 0.1 }
    );
    document.querySelectorAll('[id^="animate-"]').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  // ✅ This useEffect hook will show the popup 5 seconds after the page loads
  useEffect(() => {
    const timer = setTimeout(() => {
      // Check if popup has been shown in this session
      const popupShown = window.popupShown || false;
      if (!popupShown) {
        setShowPopup(true);
        window.popupShown = true;
      }
    }, 500); // 500ms (Changed from 5000ms to 500ms for testing visibility, assuming 500ms was intended)

    // Clean up the timer if the component unmounts
    return () => clearTimeout(timer);
  }, []);

  const handleCountryChange = (e) => {
    setSelectedCountry(e.target.value);
    if (e.target.value !== 'Other') setCustomCountry('');
  };

  const handleFormChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form Submitted:', formData, 'Country:', selectedCountry === 'Other' ? customCountry : selectedCountry);
    setShowSuccessMessage(true);
    setFormData({ firstName: '', lastName: '', email: '', phone: '', subject: '', message: '' });
    setSelectedCountry('');
    setCustomCountry('');
    setTimeout(() => setShowSuccessMessage(false), 5000);
  };

  return (
    <ThemeProvider>
      <div className="min-h-screen bg-white dark:bg-gray-950 text-gray-800 dark:text-gray-200 overflow-hidden font-sans">
        {/* Header component now contains the theme toggle button */}
        <Header />
        
        <SuccessModal show={showSuccessMessage} onClose={() => setShowSuccessMessage(false)} />

        {/* 👇 Contact Popup (auto-open) */}
        {showPopup && <ContactPopup onClose={() => setShowPopup(false)} />}

        <Hero
          stats={stats}
          countries={countries}
          selectedCountry={selectedCountry}
          customCountry={customCountry}
          handleCountryChange={handleCountryChange}
          setCustomCountry={setCustomCountry}
        />

       
        <Specializations domains={[]} isVisible={isVisible['animate-domains']} />
        <Platform features={features} isVisible={isVisible['animate-features']} />
        <Testimonials testimonials={testimonials} isVisible={isVisible['animate-testimonials']} />
        <Contact
          isVisible={isVisible['animate-contact']}
          formData={formData}
          handleFormChange={handleFormChange}
          handleSubmit={handleSubmit}
          selectedCountry={selectedCountry}
          handleCountryChange={handleCountryChange}
          customCountry={customCountry}
          setCustomCountry={setCustomCountry}
          countries={countries}
          subjectOptions={subjectOptions}
        />
        {/* FOOTER REMOVED: The Footer component is now expected to be in a global layout component. */}
      </div>
    </ThemeProvider>
  );
}